import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps } from "react";

import { cn } from "@/lib/utils/cn";

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-[0.9375rem] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0",
    {
        variants: {
            variant: {
                default: "bg-primary text-primary-foreground hover:bg-primary/90",
                destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
                secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
                outline: "border bg-background hover:bg-accent hover:text-accent-foreground",
                ghost: "hover:bg-accent hover:text-accent-foreground",
            },
            // 40 px+ touch targets on a phone.
            size: {
                default: "h-11 px-4 md:h-10",
                sm: "h-9 px-3",
                // Icon-only below `lg` (the label is `sr-only` there), labelled from `lg` up.
                "icon-sm": "size-10 [&_svg]:size-5 lg:w-auto lg:px-3 lg:[&_svg]:size-4",
            },
        },
        defaultVariants: { variant: "default", size: "default" },
    },
);

export function Button({
    className,
    variant,
    size,
    asChild = false,
    ...props
}: ComponentProps<"button"> & VariantProps<typeof buttonVariants> & { asChild?: boolean }) {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size }), className)} {...props} />;
}
